import type {
  FetchFeaturedPlacementProps,
  MultiPlacementResponse,
} from '@iheartjane/dm-sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { AlgoliaProduct } from '@jane/search/data-access';

import { useSDK } from '../sdkProvider';
import type { UseFetchPlacementProps } from './useFetchPlacement';

type UseFetchMultiPlacementsProps = Omit<
  UseFetchPlacementProps<FetchFeaturedPlacementProps>,
  'dependencies' | 'searchAttributes'
>;

interface UseFetchMultiPlacementsResponse<ProductShape extends object> {
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  placements: MultiPlacementResponse<ProductShape>[] | undefined;
}

export const useFetchMultiPlacements = <
  ProductShape extends object = AlgoliaProduct,
>(
  props: UseFetchMultiPlacementsProps
): UseFetchMultiPlacementsResponse<ProductShape> => {
  const { enabled = true, storeId } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [placements, setPlacements] = useState<
    MultiPlacementResponse<ProductShape>[] | undefined
  >(undefined);

  const { sdk } = useSDK();

  const fetchMultiPlacements = useCallback(async () => {
    if (!sdk) {
      return;
    }
    setIsLoading(true);
    setIsError(false);
    setIsSuccess(false);

    try {
      const response = enabled
        ? await sdk.fetchFeaturedPlacements<ProductShape>({
            excludeMenuTopRow: true,
            searchAttributes: ['*'],
            storeId,
          })
        : undefined;
      setPlacements(response);
      setIsSuccess(true);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [enabled, sdk, storeId]);

  useEffect(() => {
    fetchMultiPlacements();
  }, [fetchMultiPlacements]);

  const response = useMemo<UseFetchMultiPlacementsResponse<ProductShape>>(
    () => ({
      placements,
      isError,
      isLoading,
      isSuccess,
    }),
    [placements, isError, isLoading, isSuccess]
  );

  return response;
};
